import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  TextField,
  LinearProgress,
  Alert,
  CssBaseline,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
} from "@mui/material";
import axios from "axios";
import CreditsBanner from "../Components/CreditsNudge";
import {
  ContentBox,
  StyledButton,
  TitleText,
  SubtitleText,
  PageContainer,
} from "./Default.js";
import { Link } from "react-router-dom";
import UpgradeModal from "../Components/UpgradeModal";
import YouTubeTagsRankContent from "../Contents/YtTagsRankCheckerContent.js";
import { countries } from "countries-list";
import { message } from "../Constants/message.js";
import { Tooltip, IconButton } from "@mui/material";
import LockIcon from "@mui/icons-material/Lock";
import CommentSection from "../Components/CommentSection.js";
import { useNavigate } from "react-router-dom";

function YouTubeTagsRankChecker({ user, login, isLoggedIn }) {
  const navigate = useNavigate();
  const [videoUrl, setVideoUrl] = useState("");
  const [country, setCountry] = useState("US");
  const [loading, setLoading] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [resultData, setResultData] = useState(null);
  const [credits, setCredits] = useState({
    remaining: 0,
    availed: 0,
    total: 0,
    loading: true,
  });

  const countriesList = Object.entries(countries)
    .map(([code, country]) => ({
      code,
      name: country.name,
    }))
    .sort((a, b) => a.name.localeCompare(b.name));

  useEffect(() => {
    const primaryInputText = localStorage.getItem("primaryInputText");
    const primaryDropDown = localStorage.getItem("primary-drop-down");
    if (primaryInputText) {
      setVideoUrl(primaryInputText);
      localStorage.removeItem("primaryInputText");
    }
    if (primaryDropDown) {
      setCountry(primaryDropDown);
      localStorage.removeItem("primary-drop-down");
    }
  }, []);

  const handleUrlChange = (e) => {
    setSuccess("");
    setError("");
    setResultData(null);
    const newUrl = e.target.value;
    setVideoUrl(newUrl);
  };

  const handleCountryChange = (e) => {
    setCountry(e.target.value);
  };

  const validateUrl = (url) => {
    const pattern =
      /^(https?:\/\/)?(www\.)?(youtube\.com\/(watch\?v=|shorts\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})([&?=\w-]*)?$/i;

    const isValid = pattern.test(url.trim());

    if (!isValid) {
      setError(
        "❌ Invalid YouTube URL. Use formats like https://www.youtube.com/watch?v=VIDEO_ID, /shorts/VIDEO_ID, or youtu.be/VIDEO_ID"
      );
    }

    return isValid;
  };

  const handleCheckRank = async () => {
    if (!isLoggedIn) {
      setError(message.loginPrompt);
      setTimeout(() => {
        login();
      }, 1500);
      return;
    }

    setSuccess("");
    setError("");
    setResultData(null);

    if (!videoUrl) {
      setError("Please enter a YouTube video URL");
      return;
    }

    if (!validateUrl(videoUrl)) {
      return;
    }

    if (credits.remaining <= 0) {
      setError("Not enough credits to check tags rank.");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/youtube/tags-rank-checker`,
        {
          ytVideoUrl: videoUrl,
          selectCountry: country,
        },
        {
          withCredentials: true,
          headers: { "Content-Type": "application/json" },
        }
      );

      const data = response.data;

      if (data.errorMessage) {
        setError(data.errorMessage);
        return;
      }

      setResultData(data);
      setSuccess(data.successMessage);

      setCredits({
        remaining: data.remaining_credits,
        availed: data.availed_credits,
        total: data.total_credits,
        loading: false,
      });
      if (
        [0, 2, 4, 6].includes(credits.availed) &&
        user.account_type.toLowerCase() === "free"
      ) {
        setTimeout(() => {
          setShowUpgradeModal(true);
        }, 6500);
      }
    } catch (err) {
      setError(
        err.response?.data?.errorMessage ||
          "Unable to get the YouTube tags. Please check your input and try again."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {isLoggedIn && (
        <CreditsBanner credits={credits} setCredits={setCredits} />
      )}
      <CssBaseline />
      <PageContainer sx={{ paddingTop: 3 }}>
        <ContentBox>
          <TitleText variant="h3" align="center">
            YouTube Tags Rank Checker
          </TitleText>

          <SubtitleText variant="subtitle1" align="center">
            Check how your video tags rank in different countries
          </SubtitleText>

          <Box mb={3}>
            <TextField
              fullWidth
              label="YouTube Video URL"
              variant="outlined"
              value={videoUrl}
              onChange={handleUrlChange}
              placeholder="https://www.youtube.com/watch?v=VIDEO_ID"
              size="medium"
              id="youtube-video-url"
              name="primary-textbox"
              sx={{ mb: 2 }}
              error={!!error}
              helperText={error}
            />

            <FormControl fullWidth>
              <InputLabel id="country-select-label">Country</InputLabel>
              <Select
                labelId="country-select-label"
                value={country}
                label="Country"
                onChange={handleCountryChange}
                name="primary-drop-down"
              >
                {countriesList.map((country) => (
                  <MenuItem key={country.code} value={country.code}>
                    {country.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          <Box display="flex" flexDirection="column" gap={1} mb={2}>
            <Typography variant="body2" color="text.secondary">
              <b>1 Check = 1 Credit</b>
            </Typography>
            <Typography variant="body2" color="text.secondary">
              🔔 Results may take a few moments to process depending on the
              number of tags.
            </Typography>
          </Box>

          {loading && <LinearProgress sx={{ mb: 2 }} />}

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {success && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {success}
            </Alert>
          )}

          <Box display="flex" justifyContent="center" gap={2}>
            <StyledButton
              variant="contained"
              color="success"
              onClick={handleCheckRank}
              disabled={loading}
            >
              Check Tags Rank
            </StyledButton>
          </Box>
          <Box mt={3}>
            <Typography variant="body2" color="text.secondary" align="center">
              Enter the video URL and select a country to check tag rankings.
              Have questions?{" "}
              <Link to="/contact" color="primary">
                Please contact us
              </Link>
            </Typography>
          </Box>

          {resultData?.tagInfo && (
            <Box mt={4}>
              <Typography variant="h6" gutterBottom>
                Tag Rankings for{" "}
                {countriesList.find((c) => c.code === country)?.name}
              </Typography>

              <Box mb={2}>
                <Typography variant="body2">
                  <strong>Video URL:</strong> {resultData.userInput}
                </Typography>
              </Box>

              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Keyword</TableCell>
                      <TableCell align="right">Rank</TableCell>
                      <TableCell align="right">Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {resultData.tagInfo
                      .slice()
                      .sort((a, b) => {
                        if (a.rank && b.rank) {
                          return a.rank - b.rank; // sort ascending
                        } else if (a.rank) {
                          return -1; // a ranked → first
                        } else if (b.rank) {
                          return 1; // b ranked → first
                        }
                        return 0; // both unranked
                      })
                      .map((tag, index) => {
                        const isMasked =
                          isLoggedIn &&
                          user.account_type.toLowerCase() === "free" &&
                          index < 1; // 👈 mask first 1
                        return (
                          <TableRow key={index}>
                            <TableCell>{tag.keyword}</TableCell>
                            <TableCell align="right">
                              {isMasked ? (
                                <Tooltip title="Upgrade to see this tag's rank">
                                  <span
                                    style={{
                                      filter: "blur(3px)",
                                      cursor: "pointer",
                                    }}
                                    onClick={() =>
                                      (window.location.href = "/pricing")
                                    }
                                  >
                                    {tag.rank || "?"}
                                  </span>
                                </Tooltip>
                              ) : (
                                tag.rank || "—"
                              )}
                            </TableCell>
                            <TableCell align="right">
                              {isMasked ? (
                                <Chip
                                  label="Upgrade to see top 🏆 tags"
                                  color="warning"
                                  size="small"
                                  onClick={() =>
                                    (window.location.href = "/pricing")
                                  }
                                  clickable
                                />
                              ) : tag.rank ? (
                                <Chip
                                  label="Ranked"
                                  color="success"
                                  size="small"
                                />
                              ) : (
                                <Chip
                                  label="Not Ranked"
                                  color="default"
                                  size="small"
                                />
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                  </TableBody>
                </Table>
              </TableContainer>

              <Box mt={2}>
                <Typography variant="body2" color="text.secondary">
                  <strong>Stats:</strong>{" "}
                  {isLoggedIn && user.account_type.toLowerCase() === "free" ? (
                    <Tooltip title="Upgrade to see this count" arrow>
                      <span
                        style={{
                          filter: "blur(3px)",
                          cursor: "pointer",
                          display: "inline-block",
                        }}
                        onClick={() => (window.location.href = "/pricing")}
                      >
                        {resultData.tagInfo.filter((t) => t.rank).length}
                      </span>
                    </Tooltip>
                  ) : (
                    resultData.tagInfo.filter((t) => t.rank).length
                  )}{" "}
                  ranked tags out of {resultData.tagInfo.length}
                </Typography>
              </Box>
            </Box>
          )}
        </ContentBox>
        <CommentSection
          toolId="youtube/tags-rank-checker"
          user={user}
          login={login}
          isLoggedIn={isLoggedIn}
        />
        <YouTubeTagsRankContent />
        <UpgradeModal
          open={showUpgradeModal}
          onClose={() => setShowUpgradeModal(false)}
          onUpgrade={() => {
            navigate("/Pricing");
            setShowUpgradeModal(false);
          }}
          title="🎉 Hurry! 50% OFF on Annual Plans!"
          subTitle="Get full tag ranking insights — upgrade your plan today!"
        />
      </PageContainer>
    </>
  );
}

export default YouTubeTagsRankChecker;
